/**
 * @Description Fasat Grid Utils for Fasat Grid Action Body Generator
 * @FileName fasatGridUtil.js
 * @Author ABHISEK KUNDU-kundabh
 * @CreatedOn 10 February, 2021 09:52:57
 * @IssueID -323
 */
import React, { useCallback } from 'react';
import { colors } from '@manulife/mux';
import {
  Chart4, DateRange, Delete, Edit, Play1, Eye1, Download,
  Duplicate, ButtonXOutlined, Comment, Share,
  Play2,
  Calendar } from '@manulife/mux-cds-icons';
import { useSelector } from 'react-redux';
import { Logger } from '../../util';
import FasatActionBtn from '../fasatActionBtn/fasatActionBtn';
import FasatLink from '../fasatLink/fasatLink';
import { ACTION_TYPE } from './fasatGridMetadata';
import FasatExpandableParagraph from '../fasatExpandableParagraph/fasatExpandableParagraph';

const dateBodyGenerator = (dateFormat) => {
  function dateBody(rowData, columnData) {
    Logger.info(`Going to fetch data...., ${rowData}, ${columnData}, ${dateFormat}`);
    return rowData[columnData.field];
  }
  return dateBody;
};

const rowDataThree = 3;
const sharedCountThree = 3;

const sortByColumnOrder = (i1, i2) => {
  if (i1 && i2) {
    return i1.order - i2.order;
  }
  return 0;
};

/**
 * Method to show data in comma separated manner
 * @returns comma separated string
 */
const commaSeparatedBodyGenerator = () => function (rowData) {
  const { cell } = rowData;
  if (cell.row.original.sharedWith) {
    const sharedCount = cell.row.original.sharedWith.length;
    const collapsedContent = () => (
      <span>
        {cell.row.original.sharedWith.slice(0, rowDataThree).join(', ')}
        <span>,</span>
      </span>
    );

    const expandedContent = () => (
      <span>
        {cell.row.original.sharedWith.slice(rowDataThree, sharedCount).join(', ')}
      </span>
    );

    return (
      <>
        {sharedCount > sharedCountThree && (
        <FasatExpandableParagraph
          collapsedContent={collapsedContent()}
          expandedContent={expandedContent()}
          showLessButtonAriaLabel="Show less"
          showMoreButtonAriaLabel="Show more"
        />
        )}
        {sharedCount > 1 && sharedCount <= sharedCountThree && (
        <span>{cell.row.original.sharedWith.join(', ')}</span>
        )}
        {sharedCount === 1 && (
        <span>{cell.row.original.sharedWith[0]}</span>
        )}
      </>
    );
  }
  return (<></>);
};

function RowActionLink({ actions, action, cell, dataFieldKey, link, rowData }) {
  return (
    <FasatLink
      to={link}
      onClick={useCallback(
        () => action.actionHandler(rowData),
        [actions, dataFieldKey, link, rowData],
      )}
      key={cell.row.original[dataFieldKey]}
    >
      {cell.row.original[dataFieldKey]}
    </FasatLink>
  );
}

const linkBodyGenerator = (actions, dataFieldKey, link) => function (rowData) {
  const { cell } = rowData;
  if (actions && actions.length > 0) {
    return (
      <>
        {actions
          .sort((i1, i2) => sortByColumnOrder(i1, i2))
          .map((action) => (
            <RowActionLink
              actions={actions}
              action={action}
              cell={cell}
              dataFieldKey={dataFieldKey}
              link={link}
              rowData={rowData}
              key={action.id}
            />
          ))}
      </>
    );
  }
  return <></>;
};

const iconConfigMap = {
  [ACTION_TYPE.EDIT]: { Component: Edit, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.EDITHOVER]: { Component: Edit, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.SPLIT]: { Component: DateRange, color: colors.m_green, style: { width: '17px', height: '20px' } },
  [ACTION_TYPE.DELETE]: { Component: Delete, color: colors.m_green, style: { width: '16px', height: '19px' } },
  [ACTION_TYPE.LOAD]: { Component: Play1, color: colors.m_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.TREE]: { Component: Chart4, color: colors.m_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.FORCECOMPLETE]: { Component: ButtonXOutlined, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.FORCECOMPLETEHOVER]: { Component: ButtonXOutlined, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.SHARE]: { Component: Share, color: colors.m_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.SHAREHOVER]: { Component: Share, color: colors.m_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.SPLITHOVER]: { Component: DateRange, color: colors.dark_2_green, style: { width: '17px', height: '20px' } },
  [ACTION_TYPE.DELETEHOVER]: { Component: Delete, color: colors.dark_2_green, style: { width: '16px', height: '19px' } },
  [ACTION_TYPE.LOADHOVER]: { Component: Play1, color: colors.dark_2_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.TREEHOVER]: { Component: Chart4, color: colors.dark_2_green, style: { width: '20px', height: '20px' } },
  [ACTION_TYPE.VIEW]: { Component: Eye1, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.VIEWHOVER]: { Component: Eye1, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.DOWNLOAD]: { Component: Download, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.DOWNLOADHOVER]: { Component: Download, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.DUPLICATE]: { Component: Duplicate, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.DUPLICATEHOVER]: { Component: Duplicate, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.COMMENT]: { Component: Comment, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.COMMENTHOVER]: { Component: Comment, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.PLAY2]: { Component: Play2, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.PLAY2HOVER]: { Component: Play2, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.CALENDER]: { Component: Calendar, color: colors.m_green, style: { width: '18px', height: '18px' } },
  [ACTION_TYPE.CALENDERHOVER]: { Component: Calendar, color: colors.dark_2_green, style: { width: '18px', height: '18px' } },
};

export const generateIcon = (actionType) => {
  const iconData = iconConfigMap[actionType];

  if (!iconData) {
    throw new Error(`Invalid action type: ${actionType}`);
  }

  const { Component, color, style } = iconData;
  return <Component color={color} style={style} />;
};

export const getDisableLogic1 = ({ pdDisableLogic, deleteDisableLogic }) => (pdDisableLogic || deleteDisableLogic);
export const getDisableLogic2 = ({ btchDis, splitDisableLogic,
  dpDisableLogic }) => (btchDis || splitDisableLogic || dpDisableLogic);

const actionBtnDisable = ({ pdDisableLogic, deleteDisableLogic, btchDis, splitDisableLogic,
  dpDisableLogic }) => (getDisableLogic1({ pdDisableLogic, deleteDisableLogic })
   || getDisableLogic2({ btchDis, splitDisableLogic, dpDisableLogic }));

const setAriaLabel = (action) => (action.ariaLabel || action.name);

const fasatActBtn = ({ action, rowData, conditionalDisable, columnData, params, handleOnClickBtn }) => {
  const { isEnableChecker, ownerDisable, pdDisableLogic, deleteDisableLogic, btchDis, dpDisableLogic,
    splitDisableLogic } = params;
  const isEnableCheckerFlag = (isEnableChecker && !isEnableChecker(action, rowData, columnData));
  const diableFlag = (ownerDisable || conditionalDisable);
  return (
    <FasatActionBtn
      hoverIcon={generateIcon(action.hoverIcon)}
      key={action.id}
      icon={generateIcon(action.icon)}
      onClick={handleOnClickBtn}
      {...(isEnableCheckerFlag || (diableFlag || actionBtnDisable({ pdDisableLogic,
        deleteDisableLogic,
        btchDis,
        splitDisableLogic,
        dpDisableLogic })
      )
        ? { disabled: true }
        : {})}
      label={action.name}
      ariaLabel={setAriaLabel(action)}
    />
  );
};

const disableLogicConfig = (action, cell) => ((action.pdDisable >= new Date(cell.row.original.finishDate))
|| (action.pdDisable >= new Date(cell.row.original.startDate)
&& action.pdDisable <= new Date(cell.row.original.finishDate)));

const userDisable = ({ cell, loggedInUser, action }) => (cell.row.original.owner
  && cell.row.original.owner !== loggedInUser
  && action.iconDisabled);

const validEquality = (param1, param2) => (param1 === param2);

const rowGenarater = ({ cell, loggedInUser, action }) => {
  let conditionalDisable = false;
  let pdDisableLogic = false;
  let deleteDisableLogic = false;
  let splitDisableLogic = false;
  let btchDis = false;
  let dpDisableLogic = false;
  const ownerDisable = userDisable({ cell, loggedInUser, action });
  if (action.isDisable) {
    conditionalDisable = validEquality(cell.row.original[action.isDisable.key], action.isDisable.value);
  }
  if (action.pdDisable) {
    pdDisableLogic = disableLogicConfig(action, cell);
  }
  if (action.deleteDisable) {
    deleteDisableLogic = validEquality(cell.row.original.periodId, action.deleteDisable.totalRows);
  }
  if (action.splitDisable) {
    const d1 = new Date(cell.row.original.startDate);
    const d2 = new Date(cell.row.original.finishDate);
    splitDisableLogic = validEquality(d1.toDateString(), d2.toDateString());
  }
  if (action.batchDisable) {
    btchDis = !validEquality(cell.row.original.lastUpdatedStatusList, action.batchlastUpdatedStatus);
  }
  if (action.disableDP) {
    dpDisableLogic = true;
  }
  return { ownerDisable,
    conditionalDisable,
    pdDisableLogic,
    deleteDisableLogic,
    dpDisableLogic,
    splitDisableLogic,
    btchDis };
};

const checkUserRbac = (userClaim, action) => {
  if (action.aclkey) {
    const aclObject = userClaim?.access.find((i) => i.resource === action.aclkey);
    return aclObject?.permission === 'ENABLED';
  }
  return true;
};

const ActionBodyGenerator = (actions, isEnableChecker) => {
  const loggedUser = useSelector(
    ({ ApplicationStateRBAC }) => ApplicationStateRBAC && ApplicationStateRBAC.loggedUser,
  );
  const loggedInUser = loggedUser && loggedUser.UserId;
  const userClaim = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC.rbacData);

  return function (rowData, columnData = null) {
    const { cell } = rowData;
    if (actions && actions.length > 0) {
      return (
        <>
          {actions
            .sort((i1, i2) => sortByColumnOrder(i1, i2))
            .map((action) => {
              const { ownerDisable, conditionalDisable, pdDisableLogic,
                deleteDisableLogic, splitDisableLogic, btchDis, dpDisableLogic } = rowGenarater({ cell, loggedInUser, action });
              const params = { columnData,
                isEnableChecker,
                ownerDisable,
                pdDisableLogic,
                deleteDisableLogic,
                splitDisableLogic,
                btchDis,
                dpDisableLogic };
              return (checkUserRbac(userClaim, action) && fasatActBtn({ action, rowData, conditionalDisable, columnData, params, handleOnClickBtn: () => action.actionHandler(rowData, columnData) }));
            })}
        </>
      );
    }
    return null;
  };
};

const ActionBodyGeneratorRowLevelVisibility = (actions, isEnableChecker, checkRowLevelVisibility = () => true) => {
  const loggedUser = useSelector(
    ({ ApplicationStateRBAC }) => ApplicationStateRBAC && ApplicationStateRBAC.loggedUser,
  );
  const loggedInUser = loggedUser && loggedUser.UserId;
  const userClaim = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC.rbacData);
  return function (rowData, columnData = null) {
    const { cell } = rowData;
    if (actions && actions.length > 0) {
      return (
        <>
          {actions
            .sort((i1, i2) => sortByColumnOrder(i1, i2))
            .map((action) => {
              const { ownerDisable, conditionalDisable, pdDisableLogic,
                deleteDisableLogic, splitDisableLogic, btchDis, dpDisableLogic } = rowGenarater({ cell, loggedInUser, action });
              const params = { columnData,
                isEnableChecker,
                ownerDisable,
                pdDisableLogic,
                deleteDisableLogic,
                splitDisableLogic,
                btchDis,
                dpDisableLogic };
              return (checkRowLevelVisibility(rowData) && checkUserRbac(userClaim, action) && fasatActBtn({ action, rowData, conditionalDisable, columnData, params, handleOnClickBtn: () => action.actionHandler(rowData, columnData) }));
            })}
        </>
      );
    }
    return null;
  };
};

export { ActionBodyGenerator as actionBodyGenerator, dateBodyGenerator, linkBodyGenerator
  , commaSeparatedBodyGenerator, ActionBodyGeneratorRowLevelVisibility as actionBodyGeneratorRowLevelVisibility };
